import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Library, LogOut, ChevronRight, FolderOpen, Users, BarChart3, Target, Lightbulb, FileText, BookOpen, HelpCircle, ClipboardList, Bell, Calendar, AlertTriangle, Send, Loader2, Scale, Settings, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import SpEdKitLogo from '../components/SpEdKitLogo';
import QuickStartTutorial from '../components/QuickStartTutorial';
import AskSpEdKit from '../components/AskSpEdKit';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [recentStories, setRecentStories] = useState([]);
  const [upcomingIEPs, setUpcomingIEPs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  const [leaderTeams, setLeaderTeams] = useState([]);
  const [showNotifyDialog, setShowNotifyDialog] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifying, setNotifying] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    fetchRecentStories();
    fetchUpcomingIEPs();
    fetchLeaderTeams();
    checkAdminStatus();
    
    // Show tutorial ONLY for first-time users (check if they've created any content)
    checkFirstTimeUser();
  }, []);

  const checkFirstTimeUser = async () => {
    const hasSeenTutorial = localStorage.getItem('spedkit_tutorial_seen');
    if (hasSeenTutorial) return;
    
    try {
      // Check if user has any content (stories, goals, etc.)
      const [storiesRes, trackersRes] = await Promise.all([
        axios.get(`${API}/stories`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/iep-goal-tracker`, { withCredentials: true }).catch(() => ({ data: { trackers: [] } }))
      ]);
      
      // Stories API returns array directly, trackers API returns { trackers: [] }
      const stories = Array.isArray(storiesRes.data) ? storiesRes.data : (storiesRes.data?.stories || []);
      const trackers = trackersRes.data?.trackers || [];
      const hasContent = stories.length > 0 || trackers.length > 0;
      
      // Only show tutorial if user has never created any content
      if (!hasContent) {
        setShowTutorial(true);
      }
    } catch (error) {
      console.error('Error checking first time user:', error);
    }
  };

  const checkAdminStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/check`, { withCredentials: true });
      setIsAdmin(response.data.is_admin);
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    }
  };

  const handleTutorialComplete = () => {
    localStorage.setItem('spedkit_tutorial_seen', 'true');
    setShowTutorial(false);
  };

  const fetchRecentStories = async () => {
    try {
      const response = await axios.get(`${API}/stories`, {
        withCredentials: true
      });
      setRecentStories(response.data.slice(0, 4));
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingIEPs = async () => {
    try {
      const response = await axios.get(`${API}/iep-goal-tracker/upcoming-due-dates?days_ahead=60`, {
        withCredentials: true
      });
      setUpcomingIEPs(response.data.upcoming_due_dates || []);
    } catch (error) {
      console.error('Error fetching upcoming IEPs:', error);
    }
  };

  const fetchLeaderTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`, { withCredentials: true });
      const teams = response.data.teams || [];
      // Filter to only teams where user is the leader and has members
      const leaderOnly = teams.filter(t => t.role === 'leader' && t.member_count > 1);
      setLeaderTeams(leaderOnly);
      if (leaderOnly.length > 0) {
        setSelectedTeamId(leaderOnly[0].team_id);
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const handleNotifyTeam = async () => {
    if (!selectedTeamId) {
      toast.error('Please select a team');
      return;
    }
    
    setNotifying(true);
    try {
      const response = await axios.post(`${API}/teams/${selectedTeamId}/notify-due-ieps`, {
        days_ahead: 60,
        custom_message: notifyMessage || null
      }, { withCredentials: true });
      
      toast.success(`Notifications sent to ${response.data.sent_count} team member(s)`);
      setShowNotifyDialog(false);
      setNotifyMessage('');
    } catch (error) {
      console.error('Error sending notifications:', error);
      toast.error(error.response?.data?.detail || 'Failed to send notifications');
    } finally {
      setNotifying(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate('/');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      {/* Header */}
      <header className="w-full px-6 py-4 bg-white shadow-soft">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="cursor-pointer" onClick={() => navigate('/dashboard')}>
            <SpEdKitLogo className="w-10 h-10" textClassName="text-2xl" />
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/students')}
              variant="ghost"
              className="hidden sm:flex items-center gap-2 text-text-secondary hover:text-primary hover:bg-primary/10"
              data-testid="students-nav-btn"
            >
              <Users className="w-5 h-5" />
              Students
            </Button>
            <Button
              onClick={() => navigate('/my-stories')}
              variant="ghost"
              className="hidden sm:flex items-center gap-2 text-text-secondary hover:text-primary hover:bg-primary/10"
              data-testid="my-stories-nav-btn"
            >
              <Library className="w-5 h-5" />
              My Stories
            </Button>
            <Button
              onClick={() => setShowTutorial(true)}
              variant="ghost"
              className="hidden sm:flex items-center gap-2 text-text-secondary hover:text-primary hover:bg-primary/10"
              data-testid="help-btn"
            >
              <HelpCircle className="w-5 h-5" />
              Help
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-btn">
                  <Avatar className="h-10 w-10 border-2 border-primary/20">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-primary/10 text-primary font-heading">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <div className="px-3 py-2">
                  <p className="font-heading font-semibold text-text-primary">{user?.name}</p>
                  <p className="text-sm text-text-muted truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/students')} className="cursor-pointer" data-testid="dropdown-students">
                  <Users className="mr-2 h-4 w-4" />
                  Student Folders
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/my-stories')} className="cursor-pointer" data-testid="dropdown-my-stories">
                  <Library className="mr-2 h-4 w-4" />
                  My Stories
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowTutorial(true)} className="cursor-pointer" data-testid="dropdown-help">
                  <HelpCircle className="mr-2 h-4 w-4" />
                  Quick Start Guide
                </DropdownMenuItem>
                {isAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer text-purple-600" data-testid="dropdown-admin">
                      <Settings className="mr-2 h-4 w-4" />
                      Admin Dashboard
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive" data-testid="logout-btn">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-6 py-12 max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="mb-12 animate-fade-in-up">
          <h1 className="font-heading font-bold text-3xl sm:text-4xl text-text-primary">
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
        </div>

        {/* Quick Actions - Row 1 */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          {/* Create New Story Card */}
          <div 
            onClick={() => navigate('/create')}
            className="bg-gradient-to-br from-primary to-primary-dark rounded-2xl p-6 text-white cursor-pointer hover:shadow-float transition-all duration-300 hover:-translate-y-1"
            data-testid="create-story-card"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Plus className="w-6 h-6" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg">Create Story</h2>
                <p className="font-body text-white/80 text-xs">New social story</p>
              </div>
            </div>
            <div className="flex items-center gap-2 font-body text-white/90 text-sm">
              <span>Get started</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>

          {/* UDL Strategy Hunter Card */}
          <div 
            onClick={() => navigate('/udl-strategies')}
            className="bg-gradient-to-br from-accent to-accent/80 rounded-2xl p-6 text-white cursor-pointer hover:shadow-float transition-all duration-300 hover:-translate-y-1"
            data-testid="udl-strategies-card"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Lightbulb className="w-6 h-6" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg">UDL Strategies</h2>
                <p className="font-body text-white/80 text-xs">Find accommodations</p>
              </div>
            </div>
            <div className="flex items-center gap-2 font-body text-white/90 text-sm">
              <span>Explore</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>

          {/* IEP Goal Generator Card */}
          <div 
            onClick={() => navigate('/iep-goals')}
            className="bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] rounded-2xl p-6 text-white cursor-pointer hover:shadow-float transition-all duration-300 hover:-translate-y-1"
            data-testid="iep-goals-card"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg">IEP Goals</h2>
                <p className="font-body text-white/80 text-xs">SMART objectives</p>
              </div>
            </div>
            <div className="flex items-center gap-2 font-body text-white/90 text-sm">
              <span>Generate</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>

          {/* Present Levels Card */}
          <div 
            onClick={() => navigate('/present-levels')}
            className="bg-gradient-to-br from-[#EC4899] to-[#F472B6] rounded-2xl p-6 text-white cursor-pointer hover:shadow-float transition-all duration-300 hover:-translate-y-1"
            data-testid="present-levels-card"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg">Present Levels</h2>
                <p className="font-body text-white/80 text-xs">PLAAFP statements</p>
              </div>
            </div>
            <div className="flex items-center gap-2 font-body text-white/90 text-sm">
              <span>Write</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>
        </div>

        {/* Quick Actions - Row 2 */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {/* Student Folders Card */}
          <div 
            onClick={() => navigate('/students')}
            className="bg-white rounded-2xl p-6 border-2 border-secondary cursor-pointer hover:shadow-float transition-all duration-300 hover:-translate-y-1"
            data-testid="students-card"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center">
                <FolderOpen className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg text-text-primary">Students</h2>
                <p className="font-body text-text-secondary text-xs">Organize folders</p>
              </div>
            </div>
            <div className="flex items-center gap-2 font-body text-primary text-sm">
              <span>Manage</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>

          {/* View Library Card */}
          <div 
            onClick={() => navigate('/my-stories')}
            className="bg-white rounded-2xl p-6 border-2 border-secondary cursor-pointer hover:shadow-float transition-all duration-300 hover:-translate-y-1"
            data-testid="view-library-card"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center">
                <Library className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg text-text-primary">Library</h2>
                <p className="font-body text-text-secondary text-xs">All stories</p>
              </div>
            </div>
            <div className="flex items-center gap-2 font-body text-primary text-sm">
              <span>Browse</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>

          {/* IEP Goal Tracker Card */}
          <div 
            onClick={() => navigate('/iep-goal-tracker')}
            className="bg-white rounded-2xl p-6 border-2 border-[#6366F1]/20 cursor-pointer hover:shadow-float transition-all duration-300 hover:-translate-y-1"
            data-testid="iep-goal-tracker-card"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-[#6366F1]/10 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-[#6366F1]" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg text-text-primary">Goal Tracker</h2>
                <p className="font-body text-text-secondary text-xs">IEP progress</p>
              </div>
            </div>
            <div className="flex items-center gap-2 font-body text-[#6366F1] text-sm">
              <span>Track goals</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>

          {/* Worksheet Generator Card */}
          <div 
            onClick={() => navigate('/worksheets')}
            className="bg-white rounded-2xl p-6 border-2 border-purple-200 cursor-pointer hover:shadow-float transition-all duration-300 hover:-translate-y-1"
            data-testid="worksheet-generator-card"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <ClipboardList className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg text-text-primary">Worksheets</h2>
                <p className="font-body text-text-secondary text-xs">Progress & projects</p>
              </div>
            </div>
            <div className="flex items-center gap-2 font-body text-purple-600 text-sm">
              <span>Generate</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>

          {/* Teams Card */}
          <div 
            onClick={() => navigate('/teams')}
            className="bg-white rounded-2xl p-6 border-2 border-teal-200 cursor-pointer hover:shadow-float transition-all duration-300 hover:-translate-y-1"
            data-testid="teams-card"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg text-text-primary">Teams</h2>
                <p className="font-body text-text-secondary text-xs">Collaborate & share</p>
              </div>
            </div>
            <div className="flex items-center gap-2 font-body text-teal-600 text-sm">
              <span>Manage</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>

          {/* IEP Compliance Review Card */}
          <div 
            onClick={() => navigate('/iep-review')}
            className="bg-white rounded-2xl p-6 border-2 border-violet-200 cursor-pointer hover:shadow-float transition-all duration-300 hover:-translate-y-1"
            data-testid="iep-review-card"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-[#8B5CF6]/20 to-[#A855F7]/20 rounded-xl flex items-center justify-center">
                <Scale className="w-6 h-6 text-[#8B5CF6]" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg text-text-primary">IEP Review</h2>
                <p className="font-body text-text-secondary text-xs">Compliance check</p>
              </div>
            </div>
            <div className="flex items-center gap-2 font-body text-[#8B5CF6] text-sm">
              <span>Upload & review</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>

          {/* Support Staff Toolkit Card */}
          <div 
            onClick={() => navigate('/support-toolkit')}
            className="bg-white rounded-2xl p-6 border-2 border-orange-200 cursor-pointer hover:shadow-float transition-all duration-300 hover:-translate-y-1"
            data-testid="support-toolkit-card"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex items-center justify-center">
                <Wrench className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-lg text-text-primary">Support Toolkit</h2>
                <p className="font-body text-text-secondary text-xs">Accommodation tools</p>
              </div>
            </div>
            <div className="flex items-center gap-2 font-body text-orange-600 text-sm">
              <span>Generate tools</span>
              <ChevronRight className="w-4 h-4" />
            </div>
          </div>
        </div>

        {/* IEPs Due Soon Widget */}
        {upcomingIEPs.length > 0 && (
          <div className="mb-12 animate-fade-in-up" data-testid="ieps-due-soon-widget">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading font-bold text-xl text-text-primary flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-500" />
                IEPs Due Soon
              </h2>
              <div className="flex items-center gap-2">
                {leaderTeams.length > 0 && (
                  <Button 
                    variant="outline"
                    size="sm"
                    onClick={() => setShowNotifyDialog(true)}
                    className="text-amber-600 border-amber-200 hover:bg-amber-50 rounded-full"
                    data-testid="dashboard-notify-team-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Notify Team
                  </Button>
                )}
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/iep-goal-tracker')}
                  className="text-[#6366F1] hover:text-[#6366F1]/80"
                  data-testid="view-all-ieps-btn"
                >
                  View All
                </Button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {upcomingIEPs.slice(0, 6).map((tracker) => {
                const isOverdue = tracker.is_overdue;
                const daysUntil = tracker.days_until_due;
                const isUrgent = !isOverdue && daysUntil <= 14;
                
                return (
                  <div 
                    key={tracker.tracker_id}
                    onClick={() => navigate('/iep-goal-tracker')}
                    className={`rounded-xl p-4 cursor-pointer transition-all hover:shadow-float hover:-translate-y-1 ${
                      isOverdue 
                        ? 'bg-red-50 border-2 border-red-200' 
                        : isUrgent 
                          ? 'bg-amber-50 border-2 border-amber-200'
                          : 'bg-white border-2 border-gray-100'
                    }`}
                    data-testid={`iep-due-card-${tracker.tracker_id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-heading font-semibold text-text-primary text-sm truncate">
                          {tracker.student_name}
                        </h3>
                        <p className="text-xs text-text-muted truncate">{tracker.goal_title}</p>
                      </div>
                      {isOverdue ? (
                        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                      ) : (
                        <Calendar className="w-5 h-5 text-amber-500 flex-shrink-0" />
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-text-muted">
                        Due: {tracker.iep_due_date}
                      </span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        isOverdue 
                          ? 'bg-red-100 text-red-700' 
                          : isUrgent 
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-gray-100 text-gray-600'
                      }`}>
                        {isOverdue 
                          ? `${Math.abs(daysUntil)} days overdue` 
                          : `${daysUntil} days left`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Ask SpEdKit Section */}
        <div className="mb-12 animate-fade-in-up animation-delay-100">
          <AskSpEdKit variant="compact" />
        </div>

        {/* Recent Stories */}
        <div className="animate-fade-in-up animation-delay-200">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-heading font-bold text-2xl text-text-primary">Recent Stories</h2>
            {recentStories.length > 0 && (
              <Button 
                variant="ghost" 
                onClick={() => navigate('/my-stories')}
                className="text-primary hover:text-primary-dark"
                data-testid="view-all-stories-btn"
              >
                View All
              </Button>
            )}
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-2xl p-6 shadow-card animate-pulse">
                  <div className="w-full h-32 bg-secondary/50 rounded-xl mb-4"></div>
                  <div className="h-5 bg-secondary/50 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-secondary/50 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : recentStories.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {recentStories.map((story) => (
                <div 
                  key={story.story_id}
                  onClick={() => navigate(`/story/${story.story_id}`)}
                  className="bg-white rounded-2xl p-6 shadow-card hover:shadow-float transition-all duration-300 cursor-pointer hover:-translate-y-1"
                  data-testid={`story-card-${story.story_id}`}
                >
                  <div className="w-full h-32 bg-gradient-to-br from-primary/10 to-secondary rounded-xl mb-4 flex items-center justify-center">
                    <BookOpen className="w-12 h-12 text-primary/50" />
                  </div>
                  <h3 className="font-heading font-semibold text-text-primary mb-1 line-clamp-1">
                    {story.title}
                  </h3>
                  <p className="font-body text-sm text-text-muted">
                    For {story.student_info?.name}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-12 shadow-card text-center" data-testid="no-stories-message">
              <div className="w-20 h-20 bg-secondary/50 rounded-full flex items-center justify-center mx-auto mb-6">
                <BookOpen className="w-10 h-10 text-primary/50" />
              </div>
              <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                No stories yet
              </h3>
              <p className="font-body text-text-secondary mb-6">
                Create your first social story to help a child navigate their world.
              </p>
              <Button
                onClick={() => navigate('/create')}
                className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 py-3"
                data-testid="create-first-story-btn"
              >
                Create Your First Story
              </Button>
            </div>
          )}
        </div>
      </main>

      {/* Quick Start Tutorial */}
      <QuickStartTutorial 
        isOpen={showTutorial} 
        onClose={() => setShowTutorial(false)}
        onComplete={handleTutorialComplete}
      />

      {/* Notify Team Dialog */}
      <Dialog open={showNotifyDialog} onOpenChange={setShowNotifyDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              Notify Team About Due IEPs
            </DialogTitle>
            <DialogDescription className="font-body">
              Send email notifications to your team about upcoming IEP due dates.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {leaderTeams.length > 1 && (
              <div>
                <Label className="font-body font-semibold">Select Team</Label>
                <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select a team" />
                  </SelectTrigger>
                  <SelectContent>
                    {leaderTeams.map((team) => (
                      <SelectItem key={team.team_id} value={team.team_id}>
                        {team.name} ({team.member_count - 1} members)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Bell className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">What will be included:</p>
                  <ul className="list-disc list-inside space-y-1 text-amber-700">
                    <li>All upcoming IEP due dates (next 60 days)</li>
                    <li>Overdue IEPs (highlighted)</li>
                    <li>Student names and goal summaries</li>
                    <li>Recommended actions checklist</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div>
              <Label className="font-body font-semibold">Personal Message (Optional)</Label>
              <Textarea
                value={notifyMessage}
                onChange={(e) => setNotifyMessage(e.target.value)}
                placeholder="Add a personal note to your team..."
                className="mt-2"
                rows={3}
                data-testid="dashboard-notify-message-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowNotifyDialog(false);
                setNotifyMessage('');
              }} 
              className="rounded-full"
              disabled={notifying}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleNotifyTeam} 
              disabled={notifying || !selectedTeamId}
              className="bg-amber-500 text-white hover:bg-amber-600 rounded-full"
              data-testid="dashboard-send-notification-btn"
            >
              {notifying ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Notification
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
